import { useEffect, useRef } from 'react';

/**
 * Schedules emotion interval ticks. Restarts the full countdown only when
 * conversation transitions busy -> idle, or on first idle start.
 */
export function useEmotionIntervalScheduler({
  isEnabled,
  isBusy,
  intervalDurationMs,
  onTick
}) {
  const emotionIntervalRef = useRef(null);
  const wasConversationBusyRef = useRef(false);
  const onTickRef = useRef(onTick);

  onTickRef.current = onTick;

  const cleanupEmotionInterval = () => {
    if (emotionIntervalRef.current) {
      clearInterval(emotionIntervalRef.current);
      emotionIntervalRef.current = null;
    }
  };

  const startEmotionInterval = () => {
    cleanupEmotionInterval();
    emotionIntervalRef.current = setInterval(() => {
      onTickRef.current?.();
    }, intervalDurationMs);
  };

  useEffect(() => {
    const wasBusy = wasConversationBusyRef.current;
    wasConversationBusyRef.current = isBusy;

    if (!isEnabled) {
      cleanupEmotionInterval();
      return undefined;
    }

    const becameIdle = wasBusy && !isBusy;
    const needsInitialStart = !isBusy && !emotionIntervalRef.current;

    if (becameIdle || needsInitialStart) {
      startEmotionInterval();
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, isBusy, intervalDurationMs]);

  useEffect(() => () => cleanupEmotionInterval(), []);

  return { cleanupEmotionInterval };
}

export default useEmotionIntervalScheduler;
